'use client'

import { createContext, useContext } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/core/utils/common'

import type { ReactNode } from 'react'

/** 화면 하단 고정 영역(#fixed)의 DOM 노드. 스크롤 영역 밖에 있어야 CTA 가 제자리에 붙는다 */
export const FixedSlotContext = createContext<HTMLElement | null>(null)

/**
 * 페이지가 그리는 하단 CTA 바 — 레퍼런스 liftBars() 가 #view 의 .cta-bar/.bottombar 를 #fixed 로 옮기던 것.
 * variant 'cta' 는 가로 배치(관심 + 대화 시작), 'stack' 은 세로 배치(이 페르소나로 시작 / 직접 설정).
 * align 'detail' 은 PC 상세 2단 그리드의 오른쪽 컬럼에 맞춘다.
 */
export default function FixedBar({
  children,
  variant = 'cta',
  align = 'center',
}: {
  children: ReactNode
  variant?: 'cta' | 'stack'
  align?: 'center' | 'detail'
}) {
  const slot = useContext(FixedSlotContext)
  if (!slot) return null
  return createPortal(
    <div
      className={cn(
        'absolute inset-x-0 bottom-0 z-40 flex gap-2.5 px-4 pt-3 pb-6.5',
        // 모바일은 글래스 막대(아래 내용이 흐리게 비친다) — PC 는 오른쪽 컬럼 안에 놓여 면 없이 버튼만
        'glass-chrome shadow-[inset_0_1px_0_rgb(255_255_255/0.06)]',
        variant === 'cta' ? 'items-center' : 'flex-col gap-2',
        'over10:relative over10:bg-transparent over10:pb-4.5 over10:shadow-none over10:[backdrop-filter:none]',
        align === 'center' && 'over10:mx-auto over10:max-w-[760px]',
        // 오른쪽 컬럼 중심 = 50% + 왼쪽폭/2 + 간격/2 (레퍼런스 #view.detail-page ~ #fixed .cta-bar)
        align === 'detail' &&
          'over10:ml-[calc(50%_+_var(--dt-col-l)/2_+_var(--dt-col-gap)/2)] over10:w-[min(var(--dt-col-r),calc(100%_-_56px_-_var(--dt-col-l)_-_var(--dt-col-gap)))] over10:max-w-none over10:-translate-x-1/2'
      )}
    >
      {children}
    </div>,
    slot
  )
}
